'use client';

import { useEffect, useState } from 'react';
import styles from './TypewriterGroup.module.scss';

const TYPEWRITER_PHRASES = [
  'measuring audience engagement.',
  'launching project portals.',
  'equipping realtor teams.',
  'supporting homeowner operations.',
] as const;

const DELETE_SPEED = 34;
const TYPE_SPEED = 58;
const PHRASE_HOLD = 1800;

export default function TypewriterGroup()
{
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState<string>(TYPEWRITER_PHRASES[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() =>
  {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handlePreferenceChange = () => setPrefersReducedMotion(mediaQuery.matches);

    handlePreferenceChange();
    mediaQuery.addEventListener('change', handlePreferenceChange);

    return () => mediaQuery.removeEventListener('change', handlePreferenceChange);
  }, []);

  useEffect(() =>
  {
    const currentPhrase = TYPEWRITER_PHRASES[phraseIndex];

    if (prefersReducedMotion)
    {
      setDisplayedText(currentPhrase);
      setIsDeleting(false);
      return;
    }

    if (!isDeleting && displayedText === currentPhrase)
    {
      const timeoutId = window.setTimeout(() => setIsDeleting(true), PHRASE_HOLD);

      return () => window.clearTimeout(timeoutId);
    }

    if (isDeleting && displayedText === '')
    {
      setPhraseIndex((currentIndex) => (currentIndex + 1) % TYPEWRITER_PHRASES.length);
      setIsDeleting(false);
      return;
    }

    const nextText = isDeleting
      ? displayedText.slice(0, -1)
      : currentPhrase.slice(0, displayedText.length + 1);
    const timeoutId = window.setTimeout(
      () => setDisplayedText(nextText),
      isDeleting ? DELETE_SPEED : TYPE_SPEED
    );

    return () => window.clearTimeout(timeoutId);
  }, [displayedText, isDeleting, phraseIndex, prefersReducedMotion]);

  return (
    <div
      className={ styles.typewriterGroup }
      aria-atomic='true'
      aria-live='polite'>
      <span className={ styles.typewriterLine }>
        <span className={ styles.typewriterText }>{ displayedText }</span>
        <span className={ styles.typewriterCaret } aria-hidden='true' />
      </span>
    </div>
  );
}
